/**
 * Pure helpers for flow "island" scenes (no Svelte / store deps).
 * `scene.if.or[i]` pairs 1:1 with `scene.do.blk[i]` when using the flow multi-island envelope.
 */

const DEVICE_EXPR_OPS = ['eq', 'neq', 'ne', 'lt', 'gt', 'le', 'ge'] as const;

/** Extract `dev:…` ref from a condition JSON object (r, rr, or expression form). */
export function extractDeviceRefFromConditionJson(cond: unknown): string | null {
	if (!cond || typeof cond !== 'object') return null;
	const c = cond as Record<string, unknown>;
	if (typeof c.r === 'string' && c.r.startsWith('dev:')) return c.r;
	if (typeof c.rr === 'string' && c.rr.startsWith('dev:')) return c.rr;
	for (const op of DEVICE_EXPR_OPS) {
		const args = c[op];
		if (Array.isArray(args) && typeof (args[0] as { rr?: string })?.rr === 'string') {
			const rr = (args[0] as { rr: string }).rr;
			if (rr.startsWith('dev:')) return rr;
		}
	}
	if (Array.isArray(c.or)) {
		for (const limb of c.or) {
			const ref = extractDeviceRefFromConditionJson(limb);
			if (ref) return ref;
		}
	}
	if (Array.isArray(c.and)) {
		for (const limb of c.and) {
			const inner = (limb as { or?: unknown[] })?.or?.[0] ?? limb;
			const ref = extractDeviceRefFromConditionJson(inner);
			if (ref) return ref;
		}
	}
	return null;
}

/** Collect distinct device refs referenced by an island `if` expression tree. */
export function collectDeviceRefsFromIfExpr(ifExpr: unknown): string[] {
	const refs = new Set<string>();
	const walk = (expr: unknown) => {
		if (!expr || typeof expr !== 'object') return;
		const ref = extractDeviceRefFromConditionJson(expr);
		if (ref) refs.add(ref);
		const o = expr as { or?: unknown[]; and?: unknown[] };
		if (Array.isArray(o.or)) o.or.forEach(walk);
		if (Array.isArray(o.and)) o.and.forEach(walk);
	};
	walk(ifExpr);
	return Array.from(refs);
}

/**
 * Normalize `scene.do` for UI that expects an array of statements.
 * Flow multi-island scenes use `do: { blk: [...] }`; legacy scenes use `do: [...]`.
 *
 * Island trigger gates are transparently unwrapped so consumers see the real island body.
 */
export function normalizeSceneDoStatements(doBlock: unknown): any[] {
	let list: any[];
	if (doBlock === null || doBlock === undefined) list = [];
	else if (Array.isArray(doBlock)) list = doBlock;
	else if (typeof doBlock === 'object' && Array.isArray((doBlock as { blk?: unknown }).blk)) {
		list = (doBlock as { blk: any[] }).blk;
	} else list = [doBlock];
	return list.map(unwrapIslandGate);
}

function isPropertyInReasonGateClause(expr: unknown): boolean {
	if (!expr || typeof expr !== 'object') return false;
	const c = expr as { r?: unknown; cmpop?: unknown; cmpval?: unknown };
	return (
		c.r === 'p:_trig_reason' &&
		c.cmpop === 'inlsome' &&
		typeof c.cmpval === 'string' &&
		c.cmpval.endsWith('":')
	);
}

function isCmpopNeqTrigonlyGateClause(expr: unknown): boolean {
	if (!expr || typeof expr !== 'object') return false;
	const c = expr as {
		r?: unknown;
		cmpop?: unknown;
		cmpval?: unknown;
		trigonly?: unknown;
	};
	return (
		c.trigonly === true &&
		(c.cmpop === 'neq' || c.cmpop === 'ne') &&
		c.cmpval === false &&
		typeof c.r === 'string' &&
		c.r.startsWith('dev:')
	);
}

function isPropertyAwareGateClause(expr: unknown): boolean {
	if (!expr || typeof expr !== 'object') return false;
	const andArr = (expr as { and?: unknown[] }).and;
	if (!Array.isArray(andArr) || andArr.length !== 2) return false;
	return isCmpopNeqTrigonlyGateClause(andArr[0]) && isPropertyInReasonGateClause(andArr[1]);
}

/** True for FlowToScene per-island trigger-gate condition limbs. */
export function isIslandTriggerGateClause(expr: unknown): boolean {
	return (
		isCmpopNeqTrigonlyGateClause(expr) ||
		isPropertyAwareGateClause(expr) ||
		isPropertyInReasonGateClause(expr)
	);
}

function isTrigonlyIslandGateIf(ifExpr: unknown): boolean {
	if (!ifExpr || typeof ifExpr !== 'object') return false;
	const orArr = (ifExpr as { or?: unknown[] }).or;
	return Array.isArray(orArr) && orArr.length > 0 && orArr.every(isIslandTriggerGateClause);
}

/**
 * Island trigger gate: `{ _gui_island_gate: true, if: <trigonly match>, do: { if, do, else } }`.
 * Only passes when this island's own device caused the current scene run.
 */
export function isIslandTriggerGate(stmt: unknown): boolean {
	if (!stmt || typeof stmt !== 'object') return false;
	if ((stmt as { _gui_island_gate?: unknown })._gui_island_gate === true) return true;
	const inner = (stmt as { do?: unknown }).do;
	const gateIf = (stmt as { if?: unknown }).if;
	return (
		isTrigonlyIslandGateIf(gateIf) &&
		!!inner &&
		typeof inner === 'object' &&
		(inner as { if?: unknown }).if != null
	);
}

/** Return the inner island statement, stripping the trigger gate when present. */
export function unwrapIslandGate(stmt: any): any {
	return isIslandTriggerGate(stmt) ? (stmt as { do: any }).do : stmt;
}

/** Property segment from `dev:deviceId:r1` → `r1`. */
export function extractPropertyFromDeviceRef(ref: string): string | null {
	if (!ref.startsWith('dev:')) return null;
	const parts = ref.split(':');
	if (parts.length < 3) return null;
	return parts.slice(2).join(':');
}

/** Build canonical JEMMS gate clause for a device ref (device-level trigonly match). */
export function canonicalIslandTriggerGateClause(ref: string): Record<string, unknown> {
	return {
		r: ref,
		cmpop: 'neq',
		cmpval: false,
		trigonly: true,
		_gui_purpose: 'island_trigger_gate',
	};
}

/**
 * True when `_trig_reason` chr JSON contains this property key (e.g. `"r1":`).
 * Uses `inlsome` on `p:_trig_reason` — requires JEM cmpops string support for substring checks.
 * Not emitted by default until cloud JEM ships string `inlsome`; see `buildIslandTriggerGate`.
 */
export function propertyTriggeredInReasonClause(prop: string): Record<string, unknown> {
	const marker = `"${prop}":`;
	return {
		r: 'p:_trig_reason',
		cmpop: 'inlsome',
		cmpval: marker,
		_gui_purpose: 'island_trigger_gate',
	};
}

/** Decimal device id from `dev:40509794217345:r1`. */
export function deviceIdFromDeviceRef(ref: string): string | null {
	if (!ref.startsWith('dev:')) return null;
	const parts = ref.split(':');
	return parts.length >= 2 ? parts[1] : null;
}

/** Count island refs per device id (for same-device multi-property flows). */
export function countIslandRefsPerDeviceId(refs: string[]): Map<string, number> {
	const counts = new Map<string, number>();
	for (const ref of refs) {
		const did = deviceIdFromDeviceRef(ref);
		if (!did) continue;
		counts.set(did, (counts.get(did) ?? 0) + 1);
	}
	return counts;
}

/**
 * Per-ref gate clause. Default: trigonly device match (works on all deployed JEM).
 * Property `inlsome` match is opt-in via `usePropertyMatch` for same-device isolation once
 * cloud JEM supports string `inlsome` in cmpops.
 */
export function buildPropertyAwareGateClause(
	ref: string,
	opts?: { usePropertyMatch?: boolean },
): Record<string, unknown> {
	const canonical = canonicalIslandTriggerGateClause(ref);
	if (!opts?.usePropertyMatch) return canonical;
	const prop = extractPropertyFromDeviceRef(ref);
	if (!prop) return canonical;
	return {
		and: [canonical, propertyTriggeredInReasonClause(prop)],
		_gui_purpose: 'island_trigger_gate',
	};
}

/** Cloud JEM: enable when cmpops `inlsome` supports string substring checks. */
export const ISLAND_GATE_PROPERTY_MATCH_ENABLED = false;

/** JEM supports `neq` only — legacy gates used invalid `ne` and broke scene execution. */
function repairIslandGateCmpop(stmt: unknown): { stmt: unknown; changed: boolean } {
	if (!isIslandTriggerGate(stmt)) return { stmt, changed: false };
	const gate = stmt as { if?: { or?: unknown[] } };
	const orArr = gate.if?.or;
	if (!Array.isArray(orArr)) return { stmt, changed: false };
	let changed = false;
	const fixedOr = orArr.map((clause) => {
		if (
			clause &&
			typeof clause === 'object' &&
			(clause as { trigonly?: boolean; cmpop?: string }).trigonly === true &&
			(clause as { cmpop?: string }).cmpop === 'ne'
		) {
			changed = true;
			return { ...(clause as object), cmpop: 'neq' };
		}
		return clause;
	});
	if (!changed) return { stmt, changed: false };
	return { stmt: { ...(stmt as object), if: { ...gate.if, or: fixedOr } }, changed: true };
}

/** Per-island outer guard: OR of trigonly gate clauses (one per island device ref). */
export function buildIslandTriggerGate(
	refs: string[],
	allFlowRefs?: string[],
): { or: Record<string, unknown>[] } {
	const pool = allFlowRefs ?? refs;
	const perDev = countIslandRefsPerDeviceId(pool);
	return {
		or: refs.map((ref) => {
			const did = deviceIdFromDeviceRef(ref);
			const usePropertyMatch =
				ISLAND_GATE_PROPERTY_MATCH_ENABLED && did != null && (perDev.get(did) ?? 0) > 1;
			return buildPropertyAwareGateClause(ref, { usePropertyMatch });
		}),
	};
}

/** Convert nested sync `{ r, cmpop, cmpval }` to top-level expression trigger limb. */
export function nestedSyncConditionToTopLevelTrigger(expr: unknown): unknown {
	if (!expr || typeof expr !== 'object') return expr;
	const c = expr as Record<string, unknown>;
	if (
		typeof c.r === 'string' &&
		c.r.startsWith('dev:') &&
		typeof c.cmpop === 'string' &&
		c.cmpval !== undefined
	) {
		const op = c.cmpop === 'ne' ? 'neq' : c.cmpop;
		return {
			[op]: [{ rr: c.r }, c.cmpval],
			_gui_purpose: 'condition',
			...(c._fid != null ? { _fid: c._fid } : {}),
		};
	}
	if (Array.isArray(c.or)) {
		return { or: c.or.map(nestedSyncConditionToTopLevelTrigger) };
	}
	if (Array.isArray(c.and)) {
		return { and: c.and.map(nestedSyncConditionToTopLevelTrigger) };
	}
	return expr;
}

/** Build a classic top-level `if.or` limb from an island's inner nested `if`. */
export function islandInnerIfToTopLevelLimb(innerIf: unknown): unknown {
	const converted = nestedSyncConditionToTopLevelTrigger(innerIf);
	if (
		converted &&
		typeof converted === 'object' &&
		Array.isArray((converted as { or?: unknown[] }).or) &&
		(converted as { or: unknown[] }).or.length === 1 &&
		!Array.isArray((converted as { and?: unknown[] }).and)
	) {
		return (converted as { or: [unknown] }).or[0];
	}
	return converted;
}

export function isClassicFlowIslandScene(scene: {
	if?: unknown;
	do?: unknown;
	else?: unknown;
}): boolean {
	if (isReactiveFlowIslandScene(scene)) return false;
	const rawBlk = (() => {
		const d = scene.do;
		if (d == null) return [];
		if (Array.isArray(d)) return d;
		if (typeof d === 'object' && Array.isArray((d as { blk?: unknown }).blk)) {
			return (d as { blk: unknown[] }).blk;
		}
		return [d];
	})();
	if (!rawBlk.length) return false;
	const unwrapped = rawBlk.map((s) => unwrapIslandGate(s));
	if (
		!unwrapped.every(
			(s) =>
				s &&
				typeof s === 'object' &&
				(s as { if?: unknown }).if != null &&
				'do' in (s as object),
		)
	) {
		return false;
	}
	const orArr = extractSceneIslandIfOrArray(scene.if);
	if (!orArr?.length || orArr.length !== rawBlk.length) return false;
	return !orArr.every(isReactiveSubscriptionClause);
}

export function extractSceneIslandIfOrArray(ifExpr: any): any[] | null {
	if (!ifExpr || typeof ifExpr !== 'object') return null;
	if (Array.isArray(ifExpr.or) && ifExpr.or.length > 0) return ifExpr.or;
	if (Array.isArray(ifExpr.and)) {
		for (const conj of ifExpr.and) {
			if (conj && typeof conj === 'object' && Array.isArray(conj.or) && conj.or.length > 0) {
				return conj.or;
			}
		}
	}
	return null;
}

/** Top-level reactive trigger: OR of `neq [{ rr }, null]` per device ref (fires on any property change). */
export function buildReactiveTopIfFromDeviceRefs(refs: string[]): { or: Record<string, unknown>[] } {
	return {
		or: refs.map((ref) => ({
			neq: [{ rr: ref }, null],
			_gui_purpose: 'condition',
		})),
	};
}

/** Build reactive top `if` from island `do.blk` inner sync conditions. */
export function buildReactiveTopIfFromIslandBlk(blk: unknown[]): { or: Record<string, unknown>[] } {
	const refs = new Set<string>();
	for (const stmt of blk) {
		const body = unwrapIslandGate(stmt);
		for (const ref of collectDeviceRefsFromIfExpr((body as { if?: unknown }).if)) {
			refs.add(ref);
		}
	}
	return buildReactiveTopIfFromDeviceRefs(Array.from(refs));
}

/** Reactive subscription limb: `{ neq: [{ rr: "dev:…" }, null], _gui_purpose: "condition" }`. */
export function isReactiveSubscriptionClause(x: unknown): boolean {
	if (!x || typeof x !== 'object') return false;
	const neq = (x as { neq?: unknown }).neq;
	return (
		Array.isArray(neq) &&
		typeof (neq[0] as { rr?: unknown })?.rr === 'string' &&
		(neq[1] as unknown) === null
	);
}

/**
 * Flow multi-island envelope with all-devices reactive trigger at top-level `if.or`
 * and self-contained `{ if, do, else }` statements in `do.blk` (optionally gated).
 */
export function isReactiveFlowIslandScene(scene: {
	if?: unknown;
	do?: unknown;
	else?: unknown;
}): boolean {
	const rawBlk = (() => {
		const d = scene.do;
		if (d == null) return [];
		if (Array.isArray(d)) return d;
		if (typeof d === 'object' && Array.isArray((d as { blk?: unknown }).blk)) {
			return (d as { blk: unknown[] }).blk;
		}
		return [d];
	})();
	if (!rawBlk.length) return false;

	const unwrapped = rawBlk.map((s) => unwrapIslandGate(s));
	if (
		!unwrapped.every(
			(s) =>
				s &&
				typeof s === 'object' &&
				(s as { if?: unknown }).if != null &&
				'do' in (s as object),
		)
	) {
		return false;
	}
	const orArr = extractSceneIslandIfOrArray(scene.if);
	if (!orArr?.length) return false;
	return orArr.every(isReactiveSubscriptionClause);
}

/** Strip `inlsome` property limbs — they block all islands on JEM without string cmpops support. */
function simplifyIslandGateToTrigonlyOnly(stmt: unknown): { stmt: unknown; changed: boolean } {
	if (!isIslandTriggerGate(stmt)) return { stmt, changed: false };
	const gate = stmt as { if?: { or?: unknown[] } };
	const orArr = gate.if?.or;
	if (!Array.isArray(orArr)) return { stmt, changed: false };
	let changed = false;
	const fixedOr = orArr.map((clause) => {
		if (!isPropertyAwareGateClause(clause)) return clause;
		const andArr = (clause as { and: unknown[] }).and;
		const trigonly = andArr.find(isCmpopNeqTrigonlyGateClause);
		if (!trigonly) return clause;
		changed = true;
		return trigonly;
	});
	if (!changed) return { stmt, changed: false };
	return { stmt: { ...(stmt as object), if: { ...gate.if, or: fixedOr } }, changed: true };
}

function wrapUngatedIsland(stmt: unknown): unknown {
	if (!stmt || typeof stmt !== 'object') return stmt;
	if ((stmt as { _gui_island_gate?: boolean })._gui_island_gate === true) return stmt;
	if (isIslandTriggerGate(stmt)) return stmt;
	const body = stmt as { if?: unknown };
	if (!body.if) return stmt;
	const refs = collectDeviceRefsFromIfExpr(body.if);
	if (!refs.length) return stmt;
	return {
		_gui_island_gate: true,
		if: buildIslandTriggerGate(refs),
		do: stmt,
	};
}

/**
 * Upgrade flow island scenes on load:
 * - classic per-island expression top triggers → reactive `neq [rr,null]` envelope
 * - legacy `inlsome` property gates → trigonly-only (cloud JEM compat)
 * - legacy `ne` cmpop gates → `neq`
 * - ungated `do.blk` → gated when using the reactive island envelope
 */
export function migrateIslandTriggerGatesInScene<T extends { if?: unknown; do?: unknown }>(scene: T): T {
	const raw = scene.do as { blk?: unknown[] } | undefined;
	if (!Array.isArray(raw?.blk) || !raw.blk.length) return scene;

	let changed = false;
	let ifBlock = scene.if;
	const useReactiveEnvelope = isReactiveFlowIslandScene(scene) || isClassicFlowIslandScene(scene);

	if (isClassicFlowIslandScene(scene)) {
		ifBlock = buildReactiveTopIfFromIslandBlk(raw.blk) as T['if'];
		changed = true;
	}

	const blk = raw.blk.map((stmt) => {
		if (!stmt || typeof stmt !== 'object') return stmt;

		const repairedCmp = repairIslandGateCmpop(stmt);
		if (repairedCmp.changed) {
			changed = true;
			stmt = repairedCmp.stmt;
		}

		const simplified = simplifyIslandGateToTrigonlyOnly(stmt);
		if (simplified.changed) {
			changed = true;
			stmt = simplified.stmt;
		}

		if (useReactiveEnvelope && !(stmt as { _gui_island_gate?: boolean })._gui_island_gate) {
			const wrapped = wrapUngatedIsland(stmt);
			if (wrapped !== stmt) {
				changed = true;
				stmt = wrapped;
			}
		}

		return stmt;
	});

	if (!changed) return scene;
	return { ...scene, if: ifBlock, do: { ...raw, blk } };
}
